import * as Api from '@ViewModels';
import { useQuery } from '@tanstack/react-query';
import { getSupportedPlatforms } from '../../models/utils/platformCapabilities';
import { useUserSession } from '../../models/hooks/appStateHooks';

const isSamePlatformSet = (left: readonly Api.SocialMediaType[], right: readonly Api.SocialMediaType[]) =>
	left.length === right.length && left.every(platform => right.includes(platform));

/**
 * The response has to describe exactly the platforms we asked about, and the reduction has to have
 * run over that same set — otherwise the limits would cap the post at figures nobody selected.
 * A mismatch fails the query rather than being patched up locally: the reduction is five different
 * rules and the server owns it, so recomputing one here is how the two drift apart.
 */
const verifyDescribesRequest = (
	response: Api.ISocialMediaPlatformCapabilitiesResponse,
	requested: readonly Api.SocialMediaType[]
): Api.ISocialMediaPlatformCapabilitiesResponse => {
	const described = response?.platforms?.map(capability => capability.platform) ?? [];
	if (!isSamePlatformSet(described, requested) || !response.combined) {
		throw new Error(
			`social/platformCapabilities described [${described.join(', ')}] for a request for [${requested.join(', ')}]`
		);
	}
	if (!isSamePlatformSet(response.combined.platforms ?? [], requested)) {
		throw new Error(
			`social/platformCapabilities reduced over [${(response.combined.platforms ?? []).join(
				', '
			)}] for a request for [${requested.join(', ')}]`
		);
	}
	return response;
};

/**
 * The limits the API enforces for `platforms`, plus the reduction across them. Static per platform
 * set and tiny, so a refetch on every target change is effectively free.
 *
 * `data` is undefined until it answers, and stays undefined if it cannot. Callers hold off on the
 * checks that need a number rather than substituting one.
 */
export function usePlatformCapabilitiesQuery({
	platforms,
	impersonationContext,
	enabled = true,
}: {
	platforms?: Api.SocialMediaType[];
	impersonationContext?: Api.IImpersonationContext;
	enabled?: boolean;
}) {
	const userSession = useUserSession();
	const requested = getSupportedPlatforms(platforms);
	const platformsParam = [...requested].sort().join(',');

	return useQuery({
		enabled,
		queryKey: [
			'socialPlatformCapabilities',
			{ platforms: platformsParam, accountId: impersonationContext?.account?.id },
		],
		queryFn: async () => {
			const response = await userSession.webServiceHelper.callAsync<Api.ISocialMediaPlatformCapabilitiesResponse>(
				Api.ImpersonationBroker.composeApiUrl({
					impersonationContext,
					urlPath: 'social/platformCapabilities',
					queryParams: { platforms: platformsParam },
				}),
				'GET'
			);
			return verifyDescribesRequest(response, requested);
		},
		// The limits do not change while a post is being written.
		staleTime: 1000 * 60 * 60,
		refetchOnWindowFocus: false,
		retry: 1,
		meta: {
			errorLogging: { category: 'SocialMedia', action: 'PlatformCapabilitiesQuery-Error' },
			toastErrorMessage: 'Failed to load the platform limits, so this post is not checked against them.',
		},
	});
}
